// ============================================================================
// ORGANISATION SCREEN DEFINITIONS — entities, business units, financial years
// ============================================================================
// Each definition supplies what differs per kind: columns, form fields,
// validation, request shape, view sections and kind-specific row actions.
// OrganisationMasterScreen supplies everything else.

import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect, type SelectOption, type ActionMenuItem, type SyutGridColumn, type ColumnDefinition } from 'components/ui';
import notificationService from 'services/notificationService';
import { organisationService } from 'api/services/organisationService';
import {
  ORGANISATION_TYPE_MAX_LENGTH, ORGANISATION_STATUS_OPTIONS,
  type EntityResponse, type BusinessUnitResponse, type FinancialYearResponse,
  type SaveEntityRequest, type SaveBusinessUnitRequest, type SaveFinancialYearRequest,
  type OrganisationStatusCode,
} from 'api/types';
import { OrganisationStatusBadge } from './OrganisationMasterScreen';
import type { OrganisationScreenDefinition, FieldArgs, RowActionContext } from './organisationScreen.types';

// ─── Shared field helpers ─────────────────────────────────────────────────────

const TextField: React.FC<{
  field: string; label: string; value: string; required?: boolean; maxLength?: number;
  type?: 'text' | 'email' | 'tel' | 'date'; disabled?: boolean; placeholder?: string;
  error?: string; onChange: (value: string) => void;
}> = ({ field, label, value, required, maxLength, type = 'text', disabled, placeholder, error, onChange }) => (
  <div className="form-group" data-field={field}>
    <label className="form-label" htmlFor={`org-field-${field}`}>{label}{required && <span style={{ color: 'var(--danger)' }}> *</span>}</label>
    <input
      id={`org-field-${field}`}
      type={type}
      className={`form-input ${error ? 'error' : ''}`}
      value={value}
      maxLength={maxLength}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
    {error && <span className="form-error">{error}</span>}
  </div>
);

const StatusField: React.FC<{ value: string; onChange: (v: OrganisationStatusCode) => void; disabled?: boolean }> = ({ value, onChange, disabled }) => (
  <div className="form-group" data-field="statusCode">
    <SyutSelect
      label="Status"
      options={ORGANISATION_STATUS_OPTIONS}
      value={value}
      onChange={(v) => onChange(((v as string) || 'ORG_ACTIVE') as OrganisationStatusCode)}
      isDisabled={disabled}
      isClearable={false}
      size="md"
    />
  </div>
);

const toOptions = (values: readonly string[]): SelectOption[] => values.map(v => ({ value: v, label: v }));

/** Async option loader over the tenant's active rows of a kind, excluding one id (the row itself). */
const activeRowsLoader = <T extends { id: string }>(
  tenantId: string,
  kind: 'entities' | 'business-units',
  label: (row: T) => string,
  excludeId?: string,
  extra?: Record<string, string | undefined>,
) => async (inputValue: string): Promise<SelectOption[]> => {
  if (!tenantId) return [];
  try {
    const res = await organisationService.list<T>(kind, tenantId, {
      searchTerm: inputValue || undefined, statusCode: 'ORG_ACTIVE', pageSize: 50, pageNumber: 1, ...extra,
    });
    return (res.data?.items || []).filter(r => r.id !== excludeId).map(r => ({ value: r.id, label: label(r) }));
  } catch {
    return [];
  }
};

const dateOnly = (value?: string | null) => (value ? value.slice(0, 10) : '');
const formatDate = (value?: string | null) => (value ? new Date(value).toLocaleDateString() : '-');

// ============================================================================
// ENTITIES
// ============================================================================

export interface EntityForm {
  entityCode: string;
  entityName: string;
  entityType: string;
  parentEntityId: string;
  address: string;
  contactEmail: string;
  contactPhone: string;
  statusCode: OrganisationStatusCode;
}

const EMPTY_ENTITY: EntityForm = {
  entityCode: '', entityName: '', entityType: '', parentEntityId: '',
  address: '', contactEmail: '', contactPhone: '', statusCode: 'ORG_ACTIVE',
};

const entityColumnDefinitions: ColumnDefinition[] = [
  { id: 'code', label: 'Code', defaultVisible: true, sortable: true },
  { id: 'name', label: 'Name', defaultVisible: true, sortable: true },
  { id: 'type', label: 'Type', defaultVisible: true, sortable: true },
  { id: 'parent', label: 'Parent', defaultVisible: true },
  { id: 'status', label: 'Status', defaultVisible: true, sortable: true },
  { id: 'version', label: 'Version', defaultVisible: false },
  { id: 'createdAt', label: 'Created', defaultVisible: false, sortable: true },
  { id: 'actions', label: 'Actions', required: true, defaultVisible: true, width: 80 },
];

const entityGridColumns: SyutGridColumn<EntityResponse>[] = [
  { id: 'code', header: 'Code', accessorKey: 'entityCode', size: 120 },
  { id: 'name', header: 'Name', accessorKey: 'entityName', size: 220 },
  { id: 'type', header: 'Type', accessorKey: 'entityType', size: 130 },
  { id: 'parent', header: 'Parent', accessorKey: 'parentEntityCode', size: 180, enableSorting: false,
    cell: ({ row }) => row.original.parentEntityCode ? `${row.original.parentEntityCode} - ${row.original.parentEntityName}` : '-' },
  { id: 'status', header: 'Status', accessorKey: 'statusCode', size: 110, cell: ({ row }) => <OrganisationStatusBadge statusCode={row.original.statusCode} /> },
  { id: 'version', header: 'Version', accessorKey: 'version', size: 90, enableSorting: false },
  { id: 'createdAt', header: 'Created', accessorKey: 'createdAt', size: 120, cell: ({ getValue }) => formatDate(getValue() as string) },
];

export const entityDefinition: OrganisationScreenDefinition<EntityResponse, EntityForm> = {
  kind: 'entities',
  screenCode: 'ENTITY_MASTER',
  title: 'Entities',
  description: 'Legal entities and organisational units of a tenant — the organisation master',
  noun: 'Entity',
  moduleName: 'Entities',
  searchPlaceholder: 'Search entities by code or name...',
  columnsStorageKey: 'org-entities-columns',
  defaultIcon: 'home',
  columnDefinitions: entityColumnDefinitions,
  gridColumns: entityGridColumns,
  recordCode: r => r.entityCode,
  recordName: r => r.entityName,
  emptyForm: EMPTY_ENTITY,
  populate: (mode, record) => (mode === 'edit' && record) ? {
    entityCode: record.entityCode,
    entityName: record.entityName,
    entityType: record.entityType,
    parentEntityId: record.parentEntityId || '',
    address: record.address || '',
    contactEmail: record.contactEmail || '',
    contactPhone: record.contactPhone || '',
    statusCode: (record.statusCode as OrganisationStatusCode) || 'ORG_ACTIVE',
  } : EMPTY_ENTITY,
  validate: (form) => {
    const errors: Record<string, string> = {};
    if (!form.entityCode.trim()) errors.entityCode = 'Code is required';
    else if (form.entityCode.trim().length > 20) errors.entityCode = 'Code must be 20 characters or fewer';
    if (!form.entityName.trim()) errors.entityName = 'Name is required';
    if (!form.entityType) errors.entityType = 'Type is required';
    if (form.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail)) errors.contactEmail = 'Enter a valid email address';
    return errors;
  },
  toRequest: (form): SaveEntityRequest => ({
    entityCode: form.entityCode.trim(),
    entityName: form.entityName.trim(),
    entityType: form.entityType,
    parentEntityId: form.parentEntityId || null,
    address: form.address.trim() || null,
    contactEmail: form.contactEmail.trim() || null,
    contactPhone: form.contactPhone.trim() || null,
    statusCode: form.statusCode,
  }),
  renderFields: ({ form, setForm, mode, record, tenantId, getFieldError, clearFieldError }: FieldArgs<EntityForm, EntityResponse>) => {
    const set = (patch: Partial<EntityForm>) => setForm(prev => ({ ...prev, ...patch }));
    return (
      <>
        <div className="form-grid-2">
          <TextField field="entityCode" label="Code" value={form.entityCode} required maxLength={20} disabled={mode === 'edit'}
            error={getFieldError('entityCode')} onChange={(v) => { clearFieldError('entityCode'); set({ entityCode: v.toUpperCase() }); }} />
          <TextField field="entityName" label="Name" value={form.entityName} required maxLength={200}
            error={getFieldError('entityName')} onChange={(v) => { clearFieldError('entityName'); set({ entityName: v }); }} />
        </div>
        <div className="form-grid-2">
          {/* A free code: the application that uses this tenant defines and validates it. */}
          <TextField field="entityType" label="Type" value={form.entityType} required maxLength={ORGANISATION_TYPE_MAX_LENGTH}
            placeholder="Type code as the application defines it, e.g. PRIVATE_LIMITED"
            error={getFieldError('entityType')} onChange={(v) => { clearFieldError('entityType'); set({ entityType: v }); }} />
          <div className="form-group" data-field="parentEntityId">
            <SyutSelect
              label="Parent entity"
              isAsync
              loadOptions={activeRowsLoader<EntityResponse>(tenantId, 'entities', r => `${r.entityCode} - ${r.entityName}`, record?.id)}
              defaultOptions
              cacheOptions={false}
              seedOptions={record?.parentEntityId && record.parentEntityCode ? [{ value: record.parentEntityId, label: `${record.parentEntityCode} - ${record.parentEntityName}` }] : undefined}
              value={form.parentEntityId}
              onChange={(v) => set({ parentEntityId: (v as string) || '' })}
              placeholder="None (top level)"
              isClearable
              size="md"
            />
          </div>
        </div>
        <TextField field="address" label="Address" value={form.address} maxLength={500} onChange={(v) => set({ address: v })} />
        <div className="form-grid-2">
          <TextField field="contactEmail" label="Contact email" type="email" value={form.contactEmail} maxLength={200}
            error={getFieldError('contactEmail')} onChange={(v) => { clearFieldError('contactEmail'); set({ contactEmail: v }); }} />
          <TextField field="contactPhone" label="Contact phone" type="tel" value={form.contactPhone} maxLength={50} onChange={(v) => set({ contactPhone: v })} />
        </div>
        <StatusField value={form.statusCode} onChange={(v) => set({ statusCode: v })} />
      </>
    );
  },
  viewSections: (r) => [
    { title: 'General', icon: 'info', rows: [
      { label: 'Code', value: r.entityCode },
      { label: 'Name', value: r.entityName },
      { label: 'Type', value: r.entityType },
      { label: 'Parent', value: r.parentEntityCode ? `${r.parentEntityCode} - ${r.parentEntityName}` : 'None (top level)' },
      { label: 'Status', value: <OrganisationStatusBadge statusCode={r.statusCode} /> },
    ] },
    { title: 'Contact', icon: 'mail', rows: [
      { label: 'Address', value: r.address },
      { label: 'Email', value: r.contactEmail },
      { label: 'Phone', value: r.contactPhone },
    ] },
    { title: 'Organisation master', icon: 'link', rows: [
      { label: 'Canonical id', value: <code style={{ fontSize: '0.8rem' }}>{r.id}</code> },
      { label: 'Version', value: r.version },
      { label: 'Created', value: formatDate(r.createdAt) },
      { label: 'Updated', value: formatDate(r.updatedAt) },
    ] },
  ],
};

// ============================================================================
// BUSINESS UNITS
// ============================================================================

export interface BusinessUnitForm {
  entityId: string;
  buCode: string;
  buName: string;
  buType: string;
  parentBuId: string;
  description: string;
  statusCode: OrganisationStatusCode;
}

const EMPTY_BU: BusinessUnitForm = {
  entityId: '', buCode: '', buName: '', buType: '', parentBuId: '', description: '', statusCode: 'ORG_ACTIVE',
};

const buColumnDefinitions: ColumnDefinition[] = [
  { id: 'code', label: 'Code', defaultVisible: true, sortable: true },
  { id: 'name', label: 'Name', defaultVisible: true, sortable: true },
  { id: 'entity', label: 'Entity', defaultVisible: true, sortable: true },
  { id: 'type', label: 'Type', defaultVisible: true, sortable: true },
  { id: 'parent', label: 'Parent unit', defaultVisible: true },
  { id: 'status', label: 'Status', defaultVisible: true, sortable: true },
  { id: 'version', label: 'Version', defaultVisible: false },
  { id: 'createdAt', label: 'Created', defaultVisible: false, sortable: true },
  { id: 'actions', label: 'Actions', required: true, defaultVisible: true, width: 80 },
];

const buGridColumns: SyutGridColumn<BusinessUnitResponse>[] = [
  { id: 'code', header: 'Code', accessorKey: 'buCode', size: 120 },
  { id: 'name', header: 'Name', accessorKey: 'buName', size: 220 },
  { id: 'entity', header: 'Entity', accessorKey: 'entityCode', size: 180,
    cell: ({ row }) => row.original.entityCode ? `${row.original.entityCode} - ${row.original.entityName}` : '-' },
  { id: 'type', header: 'Type', accessorKey: 'buType', size: 120 },
  { id: 'parent', header: 'Parent unit', accessorKey: 'parentBuCode', size: 180, enableSorting: false,
    cell: ({ row }) => row.original.parentBuCode ? `${row.original.parentBuCode} - ${row.original.parentBuName}` : '-' },
  { id: 'status', header: 'Status', accessorKey: 'statusCode', size: 110, cell: ({ row }) => <OrganisationStatusBadge statusCode={row.original.statusCode} /> },
  { id: 'version', header: 'Version', accessorKey: 'version', size: 90, enableSorting: false },
  { id: 'createdAt', header: 'Created', accessorKey: 'createdAt', size: 120, cell: ({ getValue }) => formatDate(getValue() as string) },
];

export const businessUnitDefinition: OrganisationScreenDefinition<BusinessUnitResponse, BusinessUnitForm> = {
  kind: 'business-units',
  screenCode: 'BUSINESS_UNIT_MASTER',
  title: 'Business Units',
  description: 'Departments, divisions and cost centres within a tenant\'s entities',
  noun: 'Business Unit',
  moduleName: 'Business Units',
  searchPlaceholder: 'Search business units by code or name...',
  columnsStorageKey: 'org-business-units-columns',
  defaultIcon: 'grid',
  columnDefinitions: buColumnDefinitions,
  gridColumns: buGridColumns,
  recordCode: r => r.buCode,
  recordName: r => r.buName,
  emptyForm: EMPTY_BU,
  populate: (mode, record) => (mode === 'edit' && record) ? {
    entityId: record.entityId,
    buCode: record.buCode,
    buName: record.buName,
    buType: record.buType,
    parentBuId: record.parentBuId || '',
    description: record.description || '',
    statusCode: (record.statusCode as OrganisationStatusCode) || 'ORG_ACTIVE',
  } : EMPTY_BU,
  validate: (form) => {
    const errors: Record<string, string> = {};
    if (!form.entityId) errors.entityId = 'Entity is required';
    if (!form.buCode.trim()) errors.buCode = 'Code is required';
    else if (form.buCode.trim().length > 20) errors.buCode = 'Code must be 20 characters or fewer';
    if (!form.buName.trim()) errors.buName = 'Name is required';
    if (!form.buType) errors.buType = 'Type is required';
    return errors;
  },
  toRequest: (form): SaveBusinessUnitRequest => ({
    entityId: form.entityId,
    buCode: form.buCode.trim(),
    buName: form.buName.trim(),
    buType: form.buType,
    parentBuId: form.parentBuId || null,
    description: form.description.trim() || null,
    statusCode: form.statusCode,
  }),
  renderFields: ({ form, setForm, mode, record, tenantId, getFieldError, clearFieldError }: FieldArgs<BusinessUnitForm, BusinessUnitResponse>) => {
    const set = (patch: Partial<BusinessUnitForm>) => setForm(prev => ({ ...prev, ...patch }));
    return (
      <>
        <div className="form-group" data-field="entityId">
          <SyutSelect
            label="Entity"
            required
            isAsync
            loadOptions={activeRowsLoader<EntityResponse>(tenantId, 'entities', r => `${r.entityCode} - ${r.entityName}`)}
            defaultOptions
            cacheOptions={false}
            seedOptions={record?.entityId && record.entityCode ? [{ value: record.entityId, label: `${record.entityCode} - ${record.entityName}` }] : undefined}
            value={form.entityId}
            onChange={(v) => { clearFieldError('entityId'); set({ entityId: (v as string) || '', parentBuId: '' }); }}
            error={getFieldError('entityId')}
            placeholder="Select the entity this unit belongs to"
            isClearable={false}
            size="md"
          />
        </div>
        <div className="form-grid-2">
          <TextField field="buCode" label="Code" value={form.buCode} required maxLength={20} disabled={mode === 'edit'}
            error={getFieldError('buCode')} onChange={(v) => { clearFieldError('buCode'); set({ buCode: v.toUpperCase() }); }} />
          <TextField field="buName" label="Name" value={form.buName} required maxLength={200}
            error={getFieldError('buName')} onChange={(v) => { clearFieldError('buName'); set({ buName: v }); }} />
        </div>
        <div className="form-grid-2">
          {/* A free code: the application that uses this tenant defines and validates it. */}
          <TextField field="buType" label="Type" value={form.buType} required maxLength={ORGANISATION_TYPE_MAX_LENGTH}
            placeholder="Type code as the application defines it, e.g. HEAD_OFFICE"
            error={getFieldError('buType')} onChange={(v) => { clearFieldError('buType'); set({ buType: v }); }} />
          <div className="form-group" data-field="parentBuId">
            <SyutSelect
              label="Parent unit"
              isAsync
              loadOptions={activeRowsLoader<BusinessUnitResponse>(tenantId, 'business-units', r => `${r.buCode} - ${r.buName}`, record?.id, { entityId: form.entityId || undefined })}
              defaultOptions
              cacheOptions={false}
              seedOptions={record?.parentBuId && record.parentBuCode ? [{ value: record.parentBuId, label: `${record.parentBuCode} - ${record.parentBuName}` }] : undefined}
              value={form.parentBuId}
              onChange={(v) => set({ parentBuId: (v as string) || '' })}
              placeholder={form.entityId ? 'None (top level)' : 'Choose the entity first'}
              isDisabled={!form.entityId}
              isClearable
              size="md"
            />
          </div>
        </div>
        <TextField field="description" label="Description" value={form.description} maxLength={500} onChange={(v) => set({ description: v })} />
        <StatusField value={form.statusCode} onChange={(v) => set({ statusCode: v })} />
      </>
    );
  },
  viewSections: (r) => [
    { title: 'General', icon: 'info', rows: [
      { label: 'Code', value: r.buCode },
      { label: 'Name', value: r.buName },
      { label: 'Entity', value: r.entityCode ? `${r.entityCode} - ${r.entityName}` : '-' },
      { label: 'Type', value: r.buType },
      { label: 'Parent unit', value: r.parentBuCode ? `${r.parentBuCode} - ${r.parentBuName}` : 'None (top level)' },
      { label: 'Description', value: r.description },
      { label: 'Status', value: <OrganisationStatusBadge statusCode={r.statusCode} /> },
    ] },
    { title: 'Organisation master', icon: 'link', rows: [
      { label: 'Canonical id', value: <code style={{ fontSize: '0.8rem' }}>{r.id}</code> },
      { label: 'Version', value: r.version },
      { label: 'Created', value: formatDate(r.createdAt) },
      { label: 'Updated', value: formatDate(r.updatedAt) },
    ] },
  ],
};

// ============================================================================
// FINANCIAL YEARS
// ============================================================================

export interface FinancialYearForm {
  fyCode: string;
  fyName: string;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
  isClosed: boolean;
  statusCode: OrganisationStatusCode;
}

const EMPTY_FY: FinancialYearForm = {
  fyCode: '', fyName: '', startDate: '', endDate: '', isCurrent: false, isClosed: false, statusCode: 'ORG_ACTIVE',
};

const fyColumnDefinitions: ColumnDefinition[] = [
  { id: 'code', label: 'Code', defaultVisible: true, sortable: true },
  { id: 'name', label: 'Name', defaultVisible: true, sortable: true },
  { id: 'startDate', label: 'Start', defaultVisible: true, sortable: true },
  { id: 'endDate', label: 'End', defaultVisible: true, sortable: true },
  { id: 'current', label: 'Current', defaultVisible: true },
  { id: 'closed', label: 'Closed', defaultVisible: true },
  { id: 'status', label: 'Status', defaultVisible: true, sortable: true },
  { id: 'version', label: 'Version', defaultVisible: false },
  { id: 'actions', label: 'Actions', required: true, defaultVisible: true, width: 80 },
];

const YesNo: React.FC<{ value: boolean; yesClass?: string }> = ({ value, yesClass = 'badge-active' }) => (
  value ? <span className={`badge ${yesClass}`}>Yes</span> : <span style={{ color: 'var(--text-muted)' }}>No</span>
);

const fyGridColumns: SyutGridColumn<FinancialYearResponse>[] = [
  { id: 'code', header: 'Code', accessorKey: 'fyCode', size: 130 },
  { id: 'name', header: 'Name', accessorKey: 'fyName', size: 200 },
  { id: 'startDate', header: 'Start', accessorKey: 'startDate', size: 120, cell: ({ getValue }) => formatDate(getValue() as string) },
  { id: 'endDate', header: 'End', accessorKey: 'endDate', size: 120, cell: ({ getValue }) => formatDate(getValue() as string) },
  { id: 'current', header: 'Current', accessorKey: 'isCurrent', size: 90, enableSorting: false, cell: ({ row }) => <YesNo value={row.original.isCurrent} /> },
  { id: 'closed', header: 'Closed', accessorKey: 'isClosed', size: 90, enableSorting: false, cell: ({ row }) => <YesNo value={row.original.isClosed} yesClass="badge-inactive" /> },
  { id: 'status', header: 'Status', accessorKey: 'statusCode', size: 110, cell: ({ row }) => <OrganisationStatusBadge statusCode={row.original.statusCode} /> },
  { id: 'version', header: 'Version', accessorKey: 'version', size: 90, enableSorting: false },
];

const financialYearRowActions = (record: FinancialYearResponse, ctx: RowActionContext): ActionMenuItem[] => {
  if (!ctx.canManage) return [];
  const items: ActionMenuItem[] = [];
  if (!record.isCurrent && !record.isClosed) {
    items.push({
      id: 'set-current', label: 'Set as current', icon: <IconSystem.check size={18} />,
      onClick: async () => {
        const ok = await ctx.confirm({
          title: 'Set current financial year',
          message: `"${record.fyCode}" becomes the tenant's current financial year. Any other current year is unset.`,
          confirmLabel: 'Set current',
        });
        if (!ok) return;
        try {
          const res = await organisationService.setCurrentFinancialYear(ctx.tenantId, record.id);
          if (!res.success) throw new Error(res.message);
          notificationService.toast.success(`"${record.fyCode}" is now the current financial year.`);
          ctx.refetch();
        } catch (err: any) {
          notificationService.toast.error(err?.response?.data?.message || err?.message || 'Could not set the current financial year.');
        }
      },
    });
  }
  if (!record.isClosed) {
    items.push({
      id: 'close', label: 'Close year', icon: <IconSystem.lock size={18} />,
      onClick: async () => {
        const ok = await ctx.confirm({
          title: 'Close financial year',
          message: `"${record.fyCode}" will be closed for transactions and will no longer be the current year. This is recorded in the organisation master.`,
          confirmLabel: 'Close year',
          variant: 'danger',
        });
        if (!ok) return;
        try {
          const res = await organisationService.closeFinancialYear(ctx.tenantId, record.id);
          if (!res.success) throw new Error(res.message);
          notificationService.toast.success(`"${record.fyCode}" closed.`);
          ctx.refetch();
        } catch (err: any) {
          notificationService.toast.error(err?.response?.data?.message || err?.message || 'Could not close the financial year.');
        }
      },
    });
  }
  return items;
};

export const financialYearDefinition: OrganisationScreenDefinition<FinancialYearResponse, FinancialYearForm> = {
  kind: 'financial-years',
  screenCode: 'FINANCIAL_YEAR_MASTER',
  title: 'Financial Years',
  description: 'Financial year periods of a tenant for applications that use them',
  noun: 'Financial Year',
  moduleName: 'Financial Years',
  searchPlaceholder: 'Search financial years by code or name...',
  columnsStorageKey: 'org-financial-years-columns',
  defaultIcon: 'calendar',
  formSize: 'sm',
  columnDefinitions: fyColumnDefinitions,
  gridColumns: fyGridColumns,
  recordCode: r => r.fyCode,
  recordName: r => r.fyName,
  emptyForm: EMPTY_FY,
  populate: (mode, record) => (mode === 'edit' && record) ? {
    fyCode: record.fyCode,
    fyName: record.fyName,
    startDate: dateOnly(record.startDate),
    endDate: dateOnly(record.endDate),
    isCurrent: record.isCurrent,
    isClosed: record.isClosed,
    statusCode: (record.statusCode as OrganisationStatusCode) || 'ORG_ACTIVE',
  } : EMPTY_FY,
  validate: (form) => {
    const errors: Record<string, string> = {};
    if (!form.fyCode.trim()) errors.fyCode = 'Code is required';
    else if (form.fyCode.trim().length > 20) errors.fyCode = 'Code must be 20 characters or fewer';
    if (!form.fyName.trim()) errors.fyName = 'Name is required';
    if (!form.startDate) errors.startDate = 'Start date is required';
    if (!form.endDate) errors.endDate = 'End date is required';
    if (form.startDate && form.endDate && form.endDate <= form.startDate) errors.endDate = 'End date must be after the start date';
    return errors;
  },
  toRequest: (form): SaveFinancialYearRequest => ({
    fyCode: form.fyCode.trim(),
    fyName: form.fyName.trim(),
    startDate: form.startDate,
    endDate: form.endDate,
    isCurrent: form.isCurrent,
    isClosed: form.isClosed,
    statusCode: form.statusCode,
  }),
  renderFields: ({ form, setForm, mode, getFieldError, clearFieldError }: FieldArgs<FinancialYearForm, FinancialYearResponse>) => {
    const set = (patch: Partial<FinancialYearForm>) => setForm(prev => ({ ...prev, ...patch }));
    return (
      <>
        <div className="form-grid-2">
          <TextField field="fyCode" label="Code" value={form.fyCode} required maxLength={20} disabled={mode === 'edit'} placeholder="FY-2026-2027"
            error={getFieldError('fyCode')} onChange={(v) => { clearFieldError('fyCode'); set({ fyCode: v.toUpperCase() }); }} />
          <TextField field="fyName" label="Name" value={form.fyName} required maxLength={100}
            error={getFieldError('fyName')} onChange={(v) => { clearFieldError('fyName'); set({ fyName: v }); }} />
        </div>
        <div className="form-grid-2">
          <TextField field="startDate" label="Start date" type="date" value={form.startDate} required
            error={getFieldError('startDate')} onChange={(v) => { clearFieldError('startDate'); clearFieldError('endDate'); set({ startDate: v }); }} />
          <TextField field="endDate" label="End date" type="date" value={form.endDate} required
            error={getFieldError('endDate')} onChange={(v) => { clearFieldError('endDate'); set({ endDate: v }); }} />
        </div>
        {mode === 'edit' && (
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
            Current: <strong>{form.isCurrent ? 'Yes' : 'No'}</strong> · Closed: <strong>{form.isClosed ? 'Yes' : 'No'}</strong>
            {' '}— change these with the row actions "Set as current" and "Close year".
          </div>
        )}
        <StatusField value={form.statusCode} onChange={(v) => set({ statusCode: v })} />
      </>
    );
  },
  viewSections: (r) => [
    { title: 'General', icon: 'calendar', rows: [
      { label: 'Code', value: r.fyCode },
      { label: 'Name', value: r.fyName },
      { label: 'Start date', value: formatDate(r.startDate) },
      { label: 'End date', value: formatDate(r.endDate) },
      { label: 'Current', value: <YesNo value={r.isCurrent} /> },
      { label: 'Closed', value: <YesNo value={r.isClosed} yesClass="badge-inactive" /> },
      { label: 'Status', value: <OrganisationStatusBadge statusCode={r.statusCode} /> },
    ] },
    { title: 'Organisation master', icon: 'link', rows: [
      { label: 'Canonical id', value: <code style={{ fontSize: '0.8rem' }}>{r.id}</code> },
      { label: 'Version', value: r.version },
      { label: 'Created', value: formatDate(r.createdAt) },
      { label: 'Updated', value: formatDate(r.updatedAt) },
    ] },
  ],
  extraRowActions: financialYearRowActions,
};
